import { parseMoney } from '../utils';

/**
 * UI-only. The sale payload still sends rate (markdown already baked in) and
 * discount_percent 0 — putting the typed % on discount_percent would double
 * the cut in computeLinesTotal.
 */
export const UI_DISCOUNT_PCT = '_ui_discount_pct';

/** Real-world rupees: 2 paise. */
export function roundMoney(value) {
  return Math.round(parseMoney(value) * 100) / 100;
}

export function roundDiscountPct(value) {
  const n = Math.round((Number(value) || 0) * 100) / 100;
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, n));
}

export function formatDiscountPct(pct) {
  return String(roundDiscountPct(pct));
}

export function lineListMrp(bl) {
  const mrp = parseMoney(bl?.mrp);
  if (mrp > 0) return mrp;
  return parseMoney(bl?.rate);
}

/** Selling rate for a typed MRP discount, rounded to 2 decimals. */
export function rateAfterMrpDiscount(mrp, pct) {
  return roundMoney(roundMoney(mrp) * (1 - roundDiscountPct(pct) / 100));
}

export function applyUserDiscountToBatchLine(bl, pct) {
  return {
    ...bl,
    rate: rateAfterMrpDiscount(lineListMrp(bl), pct),
    discount_percent: 0,
    [UI_DISCOUNT_PCT]: roundDiscountPct(pct),
  };
}

export function applyOrderDiscountPercent(lines, pct) {
  return (lines || []).map((line) => ({
    ...line,
    batchLines: (line.batchLines || []).map((bl) => applyUserDiscountToBatchLine(bl, pct)),
  }));
}

/**
 * What the Discount % cell shows.
 * After the user types, that number sticks. Until then, catalogue MRP vs rate.
 */
export function displayDiscountPct(bl) {
  if (bl?.[UI_DISCOUNT_PCT] != null && bl[UI_DISCOUNT_PCT] !== '') {
    return roundDiscountPct(bl[UI_DISCOUNT_PCT]);
  }
  const mrp = lineListMrp(bl);
  const rate = parseMoney(bl?.rate);
  const extra = Number(bl?.discount_percent) || 0;
  if (mrp <= 0) return extra;
  const payable = rate * (1 - extra / 100);
  return Math.round(((mrp - payable) / mrp) * 10000) / 100;
}

function billedBatchLines(lines) {
  const out = [];
  (lines || []).forEach((line) => {
    (line.batchLines || []).forEach((bl) => {
      if ((Number(bl.qty) || 0) > 0) out.push(bl);
    });
  });
  return out;
}

/** Shared typed % across billed lines, or null when auto / mixed. */
export function uniformUiDiscountPct(lines) {
  const billed = billedBatchLines(lines);
  if (!billed.length) return null;
  const pcts = [];
  for (let i = 0; i < billed.length; i += 1) {
    const raw = billed[i][UI_DISCOUNT_PCT];
    if (raw == null || raw === '') return null;
    pcts.push(roundDiscountPct(raw));
  }
  const first = pcts[0];
  return pcts.every((p) => p === first) ? first : null;
}

/** MRP-based audit totals. Payable still comes from computeLinesTotal / server. */
export function computeMrpOrderSummary(lines) {
  let mrpSubtotal = 0;
  let billedSubtotal = 0;
  let extraDiscount = 0;
  (lines || []).forEach((line) => {
    (line.batchLines || []).forEach((bl) => {
      const qty = Number(bl.qty) || 0;
      const mrp = lineListMrp(bl);
      const rate = parseMoney(bl.rate);
      const discPct = Number(bl.discount_percent) || 0;
      mrpSubtotal += qty * mrp;
      billedSubtotal += qty * rate;
      extraDiscount += qty * rate * (discPct / 100);
    });
  });
  const markdown = Math.max(0, mrpSubtotal - billedSubtotal);
  const discount = markdown + extraDiscount;
  const uiPct = uniformUiDiscountPct(lines);
  return {
    mrpSubtotal,
    discount,
    discountPct: uiPct != null
      ? uiPct
      : (mrpSubtotal > 0 ? (discount / mrpSubtotal) * 100 : 0),
  };
}
